import React from 'react';

import { Paper } from '@mui/material';
import LeaderboardRoundedIcon from '@mui/icons-material/LeaderboardRounded';

import {
    formatInteger,
    formatPercentage
} from '../../utils/numberUtils';
import {
    getHighlights,
    MIN_RELEVANT_VISITS,
    RANKING_SORTS
} from './conversaoLPsData';

const formatRate = (value) => (value === null ? '—' : `${formatPercentage(value)}%`);

const ConversaoLPsRanking = ({
    focusedSlotById,
    onSortChange,
    onToggleFocus,
    sortKey,
    stats,
    summary
}) => {
    const isSelectable = Boolean(onToggleFocus);
    const sortedStats = [...stats].sort(RANKING_SORTS[sortKey]);
    const { bestId, worstId } = getHighlights(stats);
    const maxRate = Math.max(...stats.map((stat) => stat.conversao ?? 0), 0);
    const averagePosition = maxRate > 0 && summary.conversao !== null ? (summary.conversao / maxRate) * 100 : null;
    const columns = [
        { key: 'visitas', label: 'Visitas' },
        { key: 'leads', label: 'Leads' },
        { key: 'conversao', label: 'Conversão' },
        { key: 'fechamento', label: 'Fechamento' }
    ];

    const renderSortButton = (column) => (
        <button
            type='button'
            key={column.key}
            className={sortKey === column.key ? 'is-active' : ''}
            aria-pressed={sortKey === column.key}
            onClick={() => onSortChange(column.key)}
        >
            {column.label}
        </button>
    );

    return (
        <Paper className='crm-lp-panel crm-lp-ranking'>
            <div className='crm-lp-panel-heading'>
                <div>
                    <p className='crm-dashboard-eyebrow'>{isSelectable ? 'Clique para comparar' : 'Ranking'}</p>
                    <h3>Conversão por landing page</h3>
                </div>
                <LeaderboardRoundedIcon />
            </div>

            {stats.length === 0
                ? <p className='crm-lp-empty'>Nenhuma landing page no filtro.</p>
                : (
                    <div className='crm-lp-ranking-table' role='table' aria-label='Conversão por landing page'>
                        <div className='crm-lp-ranking-row is-head' role='row'>
                            <span role='columnheader'>Landing page</span>
                            <span role='columnheader' className='crm-lp-ranking-bar-head'>
                                {averagePosition !== null && `Média ${formatRate(summary.conversao)}`}
                            </span>
                            {columns.map((column) => (
                                <span role='columnheader' key={column.key} className='crm-lp-ranking-sort'>
                                    {renderSortButton(column)}
                                </span>
                            ))}
                        </div>

                        {sortedStats.map((stat) => {
                            const slot = focusedSlotById?.get(stat.id);
                            const isFocused = slot !== undefined;
                            const isLowVolume = stat.visitas < MIN_RELEVANT_VISITS;
                            const rowClassName = [
                                'crm-lp-ranking-row',
                                isSelectable ? 'is-selectable' : '',
                                isFocused ? 'is-focused' : '',
                                isLowVolume ? 'is-low-volume' : ''
                            ].filter(Boolean).join(' ');
                            const rowProps = isSelectable
                                ? {
                                    tabIndex: 0,
                                    'aria-pressed': isFocused,
                                    onClick: () => onToggleFocus(stat),
                                    onKeyDown: (event) => {
                                        if (event.key !== 'Enter' && event.key !== ' ') return;
                                        event.preventDefault();
                                        onToggleFocus(stat);
                                    }
                                }
                                : {};

                            return (
                                <div
                                    key={stat.id}
                                    role='row'
                                    className={rowClassName}
                                    style={isFocused ? { '--lp-color': `var(--crm-lp-slot-${slot + 1})` } : undefined}
                                    {...rowProps}
                                >
                                    <span role='cell' className='crm-lp-ranking-name'>
                                        {isSelectable && <i aria-hidden='true' />}
                                        <strong title={stat.url || stat.nome}>{stat.nome}</strong>
                                        {stat.id === bestId && <em className='is-best'>Melhor</em>}
                                        {stat.id === worstId && <em className='is-worst'>Pior</em>}
                                        {isLowVolume && stat.visitas > 0 && <em title={`Menos de ${MIN_RELEVANT_VISITS} visitas no período`}>Pouco volume</em>}
                                    </span>
                                    <span role='cell' className='crm-lp-ranking-bar' aria-hidden='true'>
                                        <span style={{ width: `${maxRate > 0 ? ((stat.conversao ?? 0) / maxRate) * 100 : 0}%` }} />
                                        {averagePosition !== null && <b style={{ left: `${averagePosition}%` }} />}
                                    </span>
                                    <span role='cell' data-label='Visitas'>{formatInteger(stat.visitas)}</span>
                                    <span role='cell' data-label='Leads'>{formatInteger(stat.leads)}</span>
                                    <span role='cell' data-label='Conversão' className='crm-lp-ranking-rate'>{formatRate(stat.conversao)}</span>
                                    <span role='cell' data-label='Fechamento'>{formatRate(stat.fechamento)}</span>
                                </div>
                            );
                        })}
                    </div>
                )}
        </Paper>
    );
};

export default ConversaoLPsRanking;
